// TripPlaybackLayer.jsx
import React, { memo, useRef } from "react";
import { Polyline, Marker, OverlayView } from "@react-google-maps/api";

const PLAYBACK_ICON_SIZE = 40;
const PLAYBACK_ANCHOR_X = PLAYBACK_ICON_SIZE / 2;
const PLAYBACK_ANCHOR_Y = PLAYBACK_ICON_SIZE / 2;

const buildFlagDataUri = (color) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
      <path d="M5 2v24h2v-8.5h14l-2.6-4.2L21 9H7V2z" fill="${color}" stroke="#1f2937" stroke-width="1.2" />
    </svg>`
  )}`;

const START_FLAG_ICON = buildFlagDataUri("#16a34a");
const END_FLAG_ICON = buildFlagDataUri("#dc2626");

const buildFlagIcon = (url) => {
  if (window?.google?.maps?.Size && window?.google?.maps?.Point) {
    return {
      url,
      scaledSize: new window.google.maps.Size(26, 26),
      anchor: new window.google.maps.Point(6, 24),
    };
  }
  return url;
};

const TripPlaybackLayer = ({
  route = [],
  currentIndex = 0,
  currentPosition = null,
  stops = [],
  markerIcon = "",
  status = "running",
}) => {
  // keep ref for future if needed
  const _lastHeadingRef = useRef(0);
  if (!route.length) return null;

  const current = currentPosition || route[currentIndex];
  const traveled = route.slice(0, currentIndex + 1);

  const exactHeading = Number.isFinite(Number(current?.heading)) ? Number(current.heading) : 0;
  const fullRouteArrows = window?.google?.maps
    ? [
        {
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_OPEN_ARROW,
            scale: 2.2,
            strokeColor: "#ef4444",
            strokeOpacity: 0.95,
            strokeWeight: 1.4,
          },
          offset: "8%",
          repeat: "76px",
        },
      ]
    : undefined;
  const traveledRouteArrows = window?.google?.maps
    ? [
        {
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 2.8,
            strokeColor: "#b91c1c",
            fillColor: "#b91c1c",
            fillOpacity: 1,
            strokeOpacity: 1,
            strokeWeight: 1.2,
          },
          offset: "10%",
          repeat: "72px",
        },
      ]
    : undefined;

  return (
    <>
      {/* FULL ROUTE */}
      <Polyline
        path={route}
        options={{
          strokeColor: "#f87171",
          strokeWeight: 5,
          strokeOpacity: 0.88,
          zIndex: 1,
          icons: fullRouteArrows,
        }}
      />

      {/* TRAVELLED ROUTE */}
      {traveled.length > 1 && (
        <Polyline
          path={traveled}
          options={{
            strokeColor: "#b91c1c",
            strokeWeight: 7,
            strokeOpacity: 1,
            zIndex: 2,
            icons: traveledRouteArrows,
          }}
        />
      )}

      {/* START */}
      <Marker
        position={route[0]}
        icon={buildFlagIcon(START_FLAG_ICON)}
        title="Start"
      />

      {/* END */}
      <Marker
        position={route[route.length - 1]}
        icon={buildFlagIcon(END_FLAG_ICON)}
        title="End"
      />

      {/* STOPS */}
      {stops.map((s, i) => (
        <Marker
          key={i}
          position={{ lat: s.lat, lng: s.lng }}
          icon="http://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
          title={`Stop ${i + 1}`}
        />
      ))}

      {/* 🚗 MOVING VEHICLE */}
      {current && (
        <OverlayView
          position={current}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              position: "relative",
              width: 0,
              height: 0,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#991b1b",
                border: "2px solid #ffffff",
                boxShadow: "0 0 0 2px rgba(248, 113, 113, 0.28)",
                transform: "translate3d(-5px, -5px, 0)",
                zIndex: 1,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: PLAYBACK_ICON_SIZE,
                height: PLAYBACK_ICON_SIZE,
                transform: `translate3d(-${PLAYBACK_ANCHOR_X}px, -${PLAYBACK_ANCHOR_Y}px, 0) rotate(${exactHeading}deg)`,
                transition: "none",
                willChange: "transform",
                transformOrigin: "center center",
                zIndex: 2,
              }}
            >
              <img
                src={markerIcon || "https://maps.google.com/mapfiles/ms/icons/red-dot.png"}
                alt="vehicle"
                className="playback-car"
                style={{
                  width: PLAYBACK_ICON_SIZE,
                  height: PLAYBACK_ICON_SIZE,
                  display: "block",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.35))",
                }}
                draggable={false}
              />
            </div>
          </div>
        </OverlayView>
      )}
    </>
  );
};

export default memo(TripPlaybackLayer);
